/**
 * Fetches and caches the public frontend runtime configuration from the backend.
 *
 * The configuration is loaded once at startup via `fetchFrontendConfig()` and
 * consumed synchronously afterwards through `getFrontendConfig()`.
 */

import axios from "axios";
import { FrontendConfigResponseDto } from "../types/dtos/api/frontend-config-response.dto";

const FRONTEND_CONFIG_TIMEOUT_MS = 10_000;

/**
 * Fallback configuration used when the backend is unreachable or has not
 * responded yet. It preserves the historical defaults for OpenTelemetry and
 * feature flags; SwissPass fields are intentionally empty so that login fails
 * with a clear message if the backend never provides them.
 */
const FALLBACK_CONFIG: FrontendConfigResponseDto = {
    "swissPass": {
        "configurationLink": "",
        "clientId":          "",
        "loginRedirectUri":  "",
    },
    "featureFlags": {
        "navigationEnabled":        false,
        "memoryProfilingEnabled":   false,
        "remoteLoggingEnabled":     true,
        "appSessionRestoreEnabled": true,
    } as FrontendConfigResponseDto["featureFlags"],
    "openTelemetry": {
        "browserExportEnabled":       false,
        "serviceName":                "flowride-frontend",
        "exporterOtlpTracesEndpoint": "",
        "tracesSamplerRatio":         1,
    },
};

let configPromise: Promise<FrontendConfigResponseDto> | null = null;
let resolvedConfig: FrontendConfigResponseDto | null = null;

/**
 * Validates that the response roughly matches the expected DTO shape.
 *
 * @param data - Raw axios response body.
 * @returns Parsed configuration, or fallback if the payload is invalid.
 */
function normalizeConfig( data: unknown ): FrontendConfigResponseDto {
    if ( data == null || typeof data !== "object" ) {
        console.warn( "[FrontendConfigApi] Invalid frontend config response; using fallback" );

        return FALLBACK_CONFIG;
    }

    const payload = data as Partial<FrontendConfigResponseDto>;

    const featureFlagsPayload = payload.featureFlags as unknown as Record<string, boolean | undefined> | undefined;

    return {
        "swissPass": {
            "configurationLink": typeof payload.swissPass?.configurationLink === "string" ? payload.swissPass.configurationLink : "",
            "clientId":          typeof payload.swissPass?.clientId === "string" ? payload.swissPass.clientId : "",
            "loginRedirectUri":  typeof payload.swissPass?.loginRedirectUri === "string" ? payload.swissPass.loginRedirectUri : "",
        },
        "featureFlags": {
            "navigationEnabled":        featureFlagsPayload?.navigationEnabled === true,
            "memoryProfilingEnabled":   featureFlagsPayload?.memoryProfilingEnabled === true,
            "remoteLoggingEnabled":     featureFlagsPayload?.remoteLoggingEnabled !== false,
            "appSessionRestoreEnabled": featureFlagsPayload?.appSessionRestoreEnabled !== false,
        } as FrontendConfigResponseDto["featureFlags"],
        "openTelemetry": {
            "browserExportEnabled":       payload.openTelemetry?.browserExportEnabled === true,
            "serviceName":                typeof payload.openTelemetry?.serviceName === "string" ? payload.openTelemetry.serviceName : "flowride-frontend",
            "exporterOtlpTracesEndpoint": typeof payload.openTelemetry?.exporterOtlpTracesEndpoint === "string" ? payload.openTelemetry.exporterOtlpTracesEndpoint : "",
            "tracesSamplerRatio":         typeof payload.openTelemetry?.tracesSamplerRatio === "number" && Number.isFinite( payload.openTelemetry.tracesSamplerRatio )
                ? Math.min( 1, Math.max( 0, payload.openTelemetry.tracesSamplerRatio ) )
                : 1,
        },
    };
}

/**
 * Fetches the public frontend runtime configuration from the backend.
 * Concurrent calls share the same in-flight promise (single-flight).
 *
 * @returns Resolves with the backend configuration or the fallback on failure.
 */
export async function fetchFrontendConfig(): Promise<FrontendConfigResponseDto> {
    if ( configPromise != null ) {
        return configPromise;
    }

    configPromise = ( async() => {
        try {
            const { data } = await axios.get<unknown>( "/api/frontend/config", { "timeout": FRONTEND_CONFIG_TIMEOUT_MS } );

            resolvedConfig = normalizeConfig( data );

            return resolvedConfig;
        } catch( error: unknown ) {
            const message = error instanceof Error ? error.message : String( error );
            console.warn( "[FrontendConfigApi] Failed to fetch frontend config; using fallback", message );
            resolvedConfig = FALLBACK_CONFIG;

            return resolvedConfig;
        }
    } )();

    return configPromise;
}

/**
 * Returns the resolved frontend configuration.
 * Callers should await `fetchFrontendConfig()` at startup; this getter returns
 * the fallback until the first fetch completes.
 *
 * @returns Current or fallback frontend configuration.
 */
export function getFrontendConfig(): FrontendConfigResponseDto {
    return resolvedConfig ?? FALLBACK_CONFIG;
}

/**
 * @returns SwissPass OIDC settings from the backend configuration.
 * @throws Error when SwissPass is not configured.
 */
export function getSwissPassConfig(): {
    "configurationLink": string;
    "clientId":          string;
    "redirectUri":       string;
} {
    const { configurationLink, clientId, loginRedirectUri } = getFrontendConfig().swissPass;
    if (
        configurationLink.trim() === "" ||
        clientId.trim() === "" ||
        loginRedirectUri.trim() === ""
    ) {
        throw new Error( "SwissPass configuration is not available from the backend" );
    }

    return {
        configurationLink,
        clientId,
        "redirectUri": loginRedirectUri,
    };
}

/**
 * @returns `true` when the navigation feature is enabled in the runtime config.
 */
export function isNavigationEnabled(): boolean {
    return getFrontendConfig().featureFlags.navigationEnabled === true;
}

/**
 * @returns `true` when the frontend is allowed to send logs to the backend.
 */
export function isRemoteLoggingEnabled(): boolean {
    return getFrontendConfig().featureFlags.remoteLoggingEnabled !== false;
}

/**
 * @returns `true` when in-browser memory profiling should be active.
 *          Enabled by default in Vite dev; in production it follows the backend flag.
 */
export function isMemoryProfilingEnabled(): boolean {
    return import.meta.env.DEV || getFrontendConfig().featureFlags.memoryProfilingEnabled === true;
}

/**
 * @returns OpenTelemetry runtime configuration from the backend.
 */
export function getOpenTelemetryConfig(): FrontendConfigResponseDto["openTelemetry"] {
    return getFrontendConfig().openTelemetry;
}
